import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import {
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";

import { parseISO } from "date-fns";
import {
    ISessionNote,
    TypeOfDisclosure,
    MethodOfDisclosure,
    IGetClientNotes,
    IEditRecordOfDisclosurePayload,
} from "../../../../modules/notes/types/notes.types";

import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import { RECORD_OF_DISCLOSURE_NOTE_LABELS } from "../../../../utils/constants";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface RecordOfDisclosureProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

export default function EditRecordOfDisclosure({
    noteDetails,
}: RecordOfDisclosureProps) {
    const [
        localRecordOfDisclosureOfPhiEphi,
        setLocalRecordOfDisclosureOfPhiEphi,
    ] = React.useState<Date | null>(
        noteDetails.note_content?.record_of_disclosure_of_phi_ephi
            ? parseISO(
                  noteDetails.note_content.record_of_disclosure_of_phi_ephi
              )
            : null
    );

    const isSigning = React.useRef(false);

    const [showPrompt, setShowPrompt] = React.useState(false);
    const [selectedNote, setSelectedNote] = React.useState(
        {} as IGetClientNotes
    );

    const { handleAutoSaveNote, handleEditDraftNote, handleSignNote } =
        useEditNote();

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };
    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        register,
        handleSubmit,
        getValues,
        control,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditRecordOfDisclosurePayload, { isDraft: boolean }>({
        resolver: yupResolver(
            yup.object({
                duration: yup.string(),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .nullable(),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                disclosure_to_whom: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Disclosure to whom is required"),
                    otherwise: yup.string(),
                }),
                disclosure_type: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Disclosure type is required"),
                    otherwise: yup.string().nullable(),
                }),
                purpose_of_disclosure: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Purpose of disclosure is required")
                        .min(
                            20,
                            "Purpose of disclosure must contain at least 20 characters"
                        ),
                    otherwise: yup.string(),
                }),
                consent_to_charge: yup.bool(),
                type_disclosed: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().required("Type disclosed is required"),
                    otherwise: yup.string(),
                }),
                method_of_disclosure: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .required("Method of disclosure is required"),
                    otherwise: yup.string(),
                }),
                record_of_disclosure_of_phi_ephi: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup.date().required("Field is required"),
                    otherwise: yup.date().nullable(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const isBillableNote = Boolean(noteDetails?.date_of_service);

    // Get methods of disclosure for select
    const MethodsOfDisclosureForSelect = Object.values(MethodOfDisclosure).map(
        (method) => {
            return {
                label: method,
                value: method,
            };
        }
    );

    // Get types of disclosure for select
    const TypesOfDisclosureForSelect = Object.values(TypeOfDisclosure).map(
        (form) => {
            return {
                label: form,
                value: form,
            };
        }
    );

    // Function to handle item click and set the selected note content
    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue("disclosure_to_whom", note.note_content.disclosure_to_whom);
        setValue("disclosure_type", note.note_content.disclosure_type);
        setValue("duration", note.note_content.duration);
        setValue(
            "method_of_disclosure",
            note.note_content.method_of_disclosure
        );
        setValue(
            "purpose_of_disclosure",
            note.note_content.purpose_of_disclosure
        );
        setValue("type_disclosed", note.note_content.type_disclosed);
        setValue(
            "record_of_disclosure_of_phi_ephi",
            note.note_content.record_of_disclosure_of_phi_ephi
        );
        setShowPrompt(false);
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <div>
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}
            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="edit-form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>

                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("disclosure_to_whom")}
                        label={
                            RECORD_OF_DISCLOSURE_NOTE_LABELS.disclosure_to_whom
                        }
                        defaultValue={
                            noteDetails.note_content?.disclosure_to_whom
                        }
                        hasError={!!errors.disclosure_to_whom}
                        errorText={errors.disclosure_to_whom?.message}
                        isRequired
                    />
                </div>
                <p className="note-label-req">
                    {RECORD_OF_DISCLOSURE_NOTE_LABELS.disclosure_type}
                </p>
                <div className="fg fg-space-between two flex">
                    <RadioInput
                        {...register("disclosure_type")}
                        label="Authorized disclosure"
                        value="Authorized disclosure"
                        defaultChecked={
                            noteDetails.note_content.disclosure_type ===
                            "Authorized disclosure"
                        }
                    />

                    <RadioInput
                        {...register("disclosure_type")}
                        label="Unauthorized disclosure"
                        value="Unauthorized disclosure"
                        defaultChecked={
                            noteDetails.note_content.disclosure_type ===
                            "Unauthorized disclosure"
                        }
                    />
                </div>
                {errors.disclosure_type ? (
                    <div className="note-error">
                        <p>Disclosure type is required</p>
                    </div>
                ) : null}
                <div className="fg">
                    <TextArea
                        {...register("purpose_of_disclosure")}
                        label={
                            RECORD_OF_DISCLOSURE_NOTE_LABELS.purpose_of_disclosure
                        }
                        defaultValue={
                            noteDetails.note_content?.purpose_of_disclosure
                        }
                        hasError={!!errors.purpose_of_disclosure}
                        errorText={errors.purpose_of_disclosure?.message}
                        isRequired
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="type_disclosed"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.type_disclosed
                                }
                                placeholder=""
                                options={TypesOfDisclosureForSelect}
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                hasError={!!errors.type_disclosed}
                                errorText={errors.type_disclosed?.message}
                                value={
                                    TypesOfDisclosureForSelect?.find(
                                        (disclosed) =>
                                            disclosed.value === field.value
                                    ) || null
                                }
                                isRequired
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="method_of_disclosure"
                        control={control}
                        render={({ field }) => (
                            <Select
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.method_of_disclosure
                                }
                                placeholder=""
                                options={MethodsOfDisclosureForSelect}
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                hasError={!!errors.method_of_disclosure}
                                errorText={errors.method_of_disclosure?.message}
                                value={
                                    MethodsOfDisclosureForSelect.find(
                                        (method) => method.value === field.value
                                    ) || null
                                }
                                isRequired
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="record_of_disclosure_of_phi_ephi"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    RECORD_OF_DISCLOSURE_NOTE_LABELS.record_of_disclosure_of_phi_ephi
                                }
                                onChange={(date) => {
                                    field.onChange(date);
                                    setLocalRecordOfDisclosureOfPhiEphi(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localRecordOfDisclosureOfPhiEphi}
                                hasError={
                                    !!errors.record_of_disclosure_of_phi_ephi
                                }
                                errorText="Field is required"
                            />
                        )}
                    />
                </div>

                {isBillableNote ? (
                    <Card classNames="bg-[--card-bg-dark]">
                        <FormCheckbox
                            {...register("consent_to_charge")}
                            label={
                                RECORD_OF_DISCLOSURE_NOTE_LABELS.consent_to_charge
                            }
                            defaultChecked={
                                noteDetails.note_content?.consent_to_charge
                            }
                        />
                    </Card>
                ) : null}

                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={!!errors.signature}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                        submitBtnIsDisabled={
                            isBillableNote && !watch("consent_to_charge")
                        }
                    />
                </div>
            </form>
        </div>
    );
}
